import React from 'react';
import classnames from 'classnames';
import { useNavigate } from '@tanstack/react-router';

import parse from 'html-react-parser';

import MemoryCardAssets from '../MemoryCard/Assets';
import MemoryCardStats from '../MemoryCard/Stats';
import MemoryReactions from '../Memory/Reactions';

import { formatDate } from '../../helpers';

import type { Memory } from '../../types/memory';
import type { Timeline } from '../../types/timeline';

import { useStore } from '../../store';

interface TimelineMemoryCardProps {
  memory: Memory;
  timeline: Timeline;
  mode?: 'public' | 'private';
};

const TimelineMemoryCard: React.FC<TimelineMemoryCardProps> = ({ memory, timeline, mode = 'private' }) => {
  const { profile } = useStore();
  const nav = useNavigate();

  let userType = 'you';
  if (profile?.id) {
    if (profile?.id === memory.user.id) {
      userType = 'you';
    } else if (memory.user.id === timeline.user.id) {
      userType = 'owner';
    } else {
      userType = 'contributor';
    }
  }

  return (
    <wa-card
      className="timeline-memory-card memory-card"
      onClick={() => {
        nav({ to: `/${mode === 'private' ? 'timelines' : 't'}/${timeline.id}/${memory.id}` })
      }}
    >
      <div className={classnames('timeline-view-line-dot', `timeline-view-line-dot-${userType}`)} />
      <div className="timeline-view-line-date">
        {formatDate(memory.date, 'MMM')}<br />
        {formatDate(memory.date, 'YYYY')}
      </div>
      <div className="timeline-memory-card-header wa-grid wa-gap-0">
        <div className="timeline-memory-card-header-left wa-stack wa-gap-0">
          <h4>{memory.title}</h4>
          <div className="timeline-memory-card-meta wa-cluster wa-gap-0">
            <span>
              {formatDate(memory.date)}
            </span>
            <span>
              Added by {memory.name}
            </span>
          </div>
        </div>
        <div className="timeline-memory-card-header-right">
          {profile?.id && (
            <wa-tag size="xs" className={`timeline-memory-card-${userType}`}>
              {userType.charAt(0).toUpperCase() + userType.slice(1)}
            </wa-tag>
          )}
        </div>
      </div>
      <MemoryCardAssets files={memory.assets} variant="timeline" />
      {memory.content && (
        <div className="timeline-memory-card-content">
          <p>{parse(memory.content)}</p>
        </div>
      )}
      <MemoryCardStats memory={memory} />
      {(mode === 'public' && (timeline.allow_reactions || timeline.allow_comments)) && (
        <>
          <hr />
          <div className="wa-cluster wa-gap-0 timeline-memory-card-actions">
            {timeline.allow_reactions && (
              <MemoryReactions memory={memory} />
            )}
            {timeline.allow_comments && (
              <wa-button size="s" appearance="outlined" className="timeline-memory-card-comment-button" pill onClick={(e) => { e.stopPropagation(); nav({ to: `/t/${timeline.id}/${memory.id}` }) }}>
                Leave a comment...
              </wa-button>
            )}
          </div>
        </>
      )}
    </wa-card>
  );
}

export default TimelineMemoryCard;
